import { useEffect, useState } from 'react';

/**
 * The viewport's width in px, kept current as the window is resized.
 *
 * Width is the input that decides how many tables can be shown at once, so
 * anything offering that choice has to re-evaluate it live. Reading
 * `window.innerWidth` during render instead would go stale silently: React has
 * no reason to re-render when a window is dragged, so the control would keep
 * offering yesterday's options until something unrelated happened to update.
 *
 * Deliberately not a media query. `useIsNarrowTableView` asks a yes/no question
 * and matchMedia answers it cheaply; this needs the number itself, and the set
 * of thresholds that matter grows with every table a display can fit.
 */
export const useViewportWidth = (): number => {
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 0 : window.innerWidth));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setWidth(window.innerWidth);
    // Catch up on anything missed between the initial state and this effect
    // (a resize during hydration, or a zoom applied before mount).
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return width;
};

export default useViewportWidth;
